type JSONPrimitive = string | number | boolean | null
type JSONValue = JSONPrimitive | JSONObject | JSONArray
interface JSONObject {
  [key: string]: JSONValue
}
type JSONArray = JSONValue[]

interface CompactOptions {
  compactArrays?: boolean
  removeEmptyArrays?: boolean
  isEmpty?: (value: unknown) => boolean
}

const defaultIsEmpty = (value: unknown): boolean => value === "" || value === null || value === undefined

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === "[object Object]"
}

export function compactObject<T extends Record<string, unknown>>(input: T, options: CompactOptions = {}): Partial<T> {
  const { compactArrays = true, removeEmptyArrays = false, isEmpty = defaultIsEmpty } = options

  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(input)) {
    if (isEmpty(value)) continue

    // Array handling
    if (Array.isArray(value)) {
      if (!compactArrays) {
        result[key] = value
        continue
      }

      const next = value
        .map((item) => {
          if (isPlainObject(item)) {
            const compacted = compactObject(item, options)
            return Object.keys(compacted).length > 0 ? compacted : undefined
          }

          return isEmpty(item) ? undefined : item
        })
        .filter((v) => v !== undefined)

      if (!removeEmptyArrays || next.length > 0) {
        result[key] = next
      }

      continue
    }

    // Object handling
    if (isPlainObject(value)) {
      const compacted = compactObject(value, options)

      if (Object.keys(compacted).length > 0) {
        result[key] = compacted
      }

      continue
    }

    // Primitive
    result[key] = value
  }

  return result as Partial<T>
}
